import React, { useEffect, useState } from "react";

import { Button } from "../ui/button";
import LoadingSpinner from "../LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";
import {
  type CustomReportResult,
  type CustomReportSummary,
  fetchVisibleCustomReports,
  runCustomReport,
} from "../../services/customReportsApi";

/**
 * Custom reports visible to the caller's company (migration 146). Rendered on
 * the Reports page below the built-ins. Each is a pre-approved report kind with
 * preset params; running one calls a scoped, read-only server query. The section
 * hides itself entirely when there are none.
 */
const CustomReportsSection: React.FC = () => {
  const { token } = useAuth();
  const [reports, setReports] = useState<CustomReportSummary[] | null>(null);
  const [result, setResult] = useState<CustomReportResult | null>(null);
  const [running, setRunning] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetchVisibleCustomReports(token)
      .then((r) => setReports(r.reports))
      .catch(() => setReports([]));
  }, [token]);

  const run = async (id: number): Promise<void> => {
    if (!token) return;
    setRunning(id);
    setError(null);
    try {
      setResult(await runCustomReport(token, id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setResult(null);
    } finally {
      setRunning(null);
    }
  };

  if (!reports || reports.length === 0) return null;

  return (
    <section className="mt-6">
      <h2 className="text-base font-semibold mb-2">Custom reports</h2>
      <div className="flex flex-wrap gap-2 mb-3">
        {reports.map((r) => (
          <Button
            key={r.report_id}
            type="button"
            size="sm"
            variant="outline"
            disabled={running !== null}
            onClick={() => void run(r.report_id)}
            title={r.description ?? undefined}
          >
            {running === r.report_id ? "Running…" : r.name}
          </Button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded mb-2">
          {error}
        </div>
      )}

      {running !== null && !result && (
        <div className="py-6 flex justify-center"><LoadingSpinner /></div>
      )}

      {result && (
        <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
          <div className="px-3 py-2 border-b border-secondary-200 flex items-baseline gap-3">
            <span className="text-sm font-semibold">{result.name}</span>
            <span className="text-[11px] text-secondary-500">{result.total} row(s)</span>
          </div>
          {result.rows.length === 0 ? (
            <div className="px-3 py-4 text-sm text-secondary-500">No rows.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
                <tr>
                  {result.columns.map((c) => (
                    <th key={c.key} className="text-left px-3 py-1.5 font-semibold">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {result.rows.map((row, i) => (
                  <tr key={i} className="text-secondary-700">
                    {result.columns.map((c) => (
                      <td key={c.key} className="px-3 py-1.5">
                        {row[c.key] === null || row[c.key] === undefined
                          ? "—"
                          : String(row[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
};

export default CustomReportsSection;
